import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { Card } from '../../atoms/Card/Card';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Alert } from '../../atoms/Alert/Alert';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { EmptyState } from '../../atoms/EmptyState';
import { Icon } from '../../atoms/Icon/Icon';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';
import { ApiError } from '../../../lib/apiClient';
import {
  activateReefBotTank,
  connectReefBot,
  disconnectReefBot,
  getReefBotConnection,
  getReefBotMappings,
  listReefBotTanks,
  listReefBotTargets,
  previewReefBotTank,
  reefbotConnectionKey,
  reefbotMappingsKey,
  reefbotPreviewKey,
  reefbotTanksKey,
  reefbotTargetsKey,
  saveReefBotMappings,
  syncReefBot,
  type ReefBotImportResult,
  type ReefBotMappingInput,
  type ReefBotSyncSummary,
} from '../../../services/reefbotService';
import { TestMappingTable } from '../TestMapping/TestMappingTable';
import { mappingInputs, type MappingDraft, type MappingDrafts } from '../TestMapping/testMappingDraft';
import { useToast } from '../../../stores/toastStore';
import './ReefBotPanel.css';

export interface ReefBotPanelProps {
  /** ReefGuide aquarium the ReefBot tank's results are imported into. */
  aquariumId: string | undefined;
}

const ERRORS = 'integrations.reefbot.errors';

/** Translation key of an error (never raw backend text). */
export function reefbotErrorKey(err: unknown): string {
  const code = err instanceof ApiError ? err.code : typeof err === 'string' ? err : '';
  switch (code) {
    case 'REEFBOT_AUTH_FAILED':
      return `${ERRORS}.auth`;
    case 'REEFBOT_TANK_NOT_FOUND':
      return `${ERRORS}.tankNotFound`;
    case 'REEFBOT_UNAVAILABLE':
    case 'REEFBOT_NETWORK_ERROR':
      return `${ERRORS}.unavailable`;
    case 'REEFBOT_NOT_CONNECTED':
      return `${ERRORS}.notConnected`;
    case 'RATE_LIMITED':
      return `${ERRORS}.rateLimited`;
    default:
      return err instanceof ApiError && err.status === 429 ? `${ERRORS}.rateLimited` : `${ERRORS}.generic`;
  }
}

function formatDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
}

export function ReefBotPanel({ aquariumId }: ReefBotPanelProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const headingId = useId();
  const id = aquariumId ?? '';

  // The password lives only in this component's state: never in a query key,
  // the query cache or web storage. It is cleared once the account is saved.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [changingTank, setChangingTank] = useState(false);
  const [tankChoice, setTankChoice] = useState<string | null>(null);
  // Unsaved mapping choices, by test (sourceKey); cleared when saved or the tank changes.
  const [drafts, setDrafts] = useState<MappingDrafts>({});

  const connectionQuery = useQuery({
    queryKey: reefbotConnectionKey(id),
    queryFn: () => getReefBotConnection(id),
    enabled: id !== '',
  });
  const connection = connectionQuery.data;
  const connected = connection?.connected === true;
  const activeTank = connection?.tankId ?? '';
  const choosingTank = connected && !replacing && (activeTank === '' || changingTank);

  const tanksQuery = useQuery({
    queryKey: reefbotTanksKey(id),
    queryFn: () => listReefBotTanks(id),
    enabled: choosingTank,
    retry: false,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
  const tanks = tanksQuery.data ?? [];
  // A single tank is preselected so its preview shows at once; nothing is imported before "activate".
  const selectedTank = tankChoice ?? (tanks.length === 1 ? tanks[0]?.id ?? '' : '');
  const previewTank = choosingTank && selectedTank !== '' && selectedTank !== activeTank ? selectedTank : '';

  const previewQuery = useQuery({
    queryKey: reefbotPreviewKey(id, previewTank),
    queryFn: () => previewReefBotTank(id, previewTank),
    enabled: previewTank !== '',
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const preview = previewQuery.data ?? [];

  const showMappings = connected && !replacing && activeTank !== '' && !changingTank;
  const mappingsQuery = useQuery({
    queryKey: reefbotMappingsKey(id),
    queryFn: () => getReefBotMappings(id),
    enabled: showMappings,
  });
  const mappings = mappingsQuery.data ?? [];
  const newTests = mappings.filter((m) => m.status === 'new').length;

  const targetsQuery = useQuery({
    queryKey: reefbotTargetsKey(id),
    queryFn: () => listReefBotTargets(id),
    enabled: previewTank !== '' || showMappings,
    staleTime: Infinity,
  });
  const targets = targetsQuery.data ?? [];

  const previewInputs = mappingInputs(preview, drafts);
  const importable = previewInputs?.filter((m) => m.parameterId !== null || m.customParameter).length ?? 0;
  // Stored mappings as they are — "accept suggestions" confirms every new test unchanged.
  const mappingsInputs = mappingInputs(mappings, {});

  function setDraft(sourceKey: string, draft: MappingDraft) {
    setDrafts((current) => ({ ...current, [sourceKey]: draft }));
  }

  function afterImport(summary: ReefBotSyncSummary) {
    if (summary.status === 'failed') return;
    if (summary.imported + summary.updated > 0) {
      void queryClient.invalidateQueries({ queryKey: ['measurements', id] });
    }
    showToast(t('integrations.reefbot.sync.done', { imported: summary.imported, updated: summary.updated }), 'success');
  }

  const connectMutation = useMutation({
    mutationFn: () => connectReefBot(id, email, password),
    onSuccess: (result) => {
      setPassword('');
      setTouched(false);
      setReplacing(false);
      setChangingTank(false);
      setTankChoice(null);
      queryClient.setQueryData(reefbotConnectionKey(id), result.connection);
      queryClient.setQueryData(reefbotTanksKey(id), result.tanks);
    },
  });

  function afterSave(result: ReefBotImportResult) {
    setDrafts({});
    // A parameter created for a test is offered for other tests from now on.
    void queryClient.invalidateQueries({ queryKey: reefbotTargetsKey(id) });
    queryClient.setQueryData(reefbotConnectionKey(id), result.connection);
    queryClient.setQueryData(reefbotMappingsKey(id), result.mappings);
    afterImport(result.sync);
  }

  const activateMutation = useMutation({
    mutationFn: (input: { tankId: string; mappings: ReefBotMappingInput[] }) => activateReefBotTank(id, input.tankId, input.mappings),
    onSuccess: (result) => {
      setTankChoice(null);
      setChangingTank(false);
      afterSave(result);
    },
  });

  const saveMutation = useMutation({
    mutationFn: (input: ReefBotMappingInput[]) => saveReefBotMappings(id, input),
    onSuccess: afterSave,
  });

  /** Saves one test's mapping from the dialog (only that test — other new tests stay unconfirmed); rejects when it failed. */
  async function saveRow(sourceKey: string, draft: MappingDraft): Promise<void> {
    const inputs = mappingInputs(mappings.filter((m) => m.sourceKey === sourceKey), { [sourceKey]: draft });
    if (!inputs) throw new Error('invalid mapping');
    await saveMutation.mutateAsync(inputs);
  }

  const syncMutation = useMutation({
    mutationFn: () => syncReefBot(id),
    onSuccess: (result) => {
      queryClient.setQueryData(reefbotConnectionKey(id), result.connection);
      afterImport(result.sync);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectReefBot(id),
    onSuccess: () => {
      setReplacing(false);
      setChangingTank(false);
      setTankChoice(null);
      queryClient.removeQueries({ queryKey: ['reefbot', id] });
      void queryClient.invalidateQueries({ queryKey: reefbotConnectionKey(id) });
    },
  });

  const isBusy = connectMutation.isPending || activateMutation.isPending || saveMutation.isPending
    || syncMutation.isPending || disconnectMutation.isPending;

  // ── Account form ──────────────────────────────────────────────────────────
  const emailError = touched && !/^\S+@\S+\.\S+$/.test(email.trim()) ? t('integrations.reefbot.account.emailError') : undefined;
  const passwordError = touched && password === '' ? t('integrations.reefbot.account.passwordError') : undefined;

  function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (isBusy || !/^\S+@\S+\.\S+$/.test(email.trim()) || password === '') return;
    disconnectMutation.reset();
    connectMutation.mutate();
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (connectionQuery.isPending) {
    return (
      <Card variant="outlined" className="reefbot-panel">
        <Heading level={2}>{t('integrations.reefbot.title')}</Heading>
        <LiveRegion><Text color="secondary">{t('integrations.reefbot.loading')}</Text></LiveRegion>
      </Card>
    );
  }

  if (connectionQuery.isError) {
    return (
      <Card variant="outlined" className="reefbot-panel">
        <Heading level={2}>{t('integrations.reefbot.title')}</Heading>
        <LoadErrorState
          title={t('integrations.reefbot.loadError')}
          titleLevel={3}
          onRetry={() => { void connectionQuery.refetch(); }}
          retryLabel={t('integrations.reefbot.retry')}
          retrying={connectionQuery.isFetching}
        />
      </Card>
    );
  }

  const lastSync = connection?.lastSync ?? null;
  const showForm = !connected || replacing;

  const announcement = connectMutation.isPending
    ? t('integrations.reefbot.account.connecting')
    : activateMutation.isPending || saveMutation.isPending || syncMutation.isPending
      ? t('integrations.reefbot.sync.running')
      : disconnectMutation.isSuccess
        ? t('integrations.reefbot.account.disconnected')
        : '';

  return (
    <Card variant="outlined" className="reefbot-panel">
      <Stack gap="2">
        <Heading level={2}>{t('integrations.reefbot.title')}</Heading>
        <Text color="secondary">{t('integrations.reefbot.description')}</Text>
      </Stack>

      <LiveRegion visuallyHidden>{announcement}</LiveRegion>

      {/* ── Reef Kinetics account ───────────────────────────────────────── */}
      <section className="reefbot-panel__section" aria-labelledby={`${headingId}-account`}>
        <Heading level={3} id={`${headingId}-account`}>{t('integrations.reefbot.account.heading')}</Heading>

        {showForm ? (
          <form className="reefbot-panel__form" onSubmit={handleConnect} noValidate>
            <Input
              type="email"
              label={t('integrations.reefbot.account.email')}
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
              error={emailError}
              disabled={isBusy}
              required
              autoComplete="off"
              maxLength={255}
              fullWidth
            />
            <Input
              type="password"
              label={t('integrations.reefbot.account.password')}
              value={password}
              onChange={(e) => { setPassword(e.target.value); }}
              error={passwordError}
              helperText={t('integrations.reefbot.account.passwordHelp')}
              disabled={isBusy}
              required
              autoComplete="off"
              maxLength={200}
              fullWidth
            />
            <Text variant="body-sm" color="secondary">{t('integrations.reefbot.account.googleNote')}</Text>
            {connectMutation.isError && <Alert compact>{t(reefbotErrorKey(connectMutation.error))}</Alert>}
            <Inline gap="3" wrap>
              <Button type="submit" variant="primary" isLoading={connectMutation.isPending} disabled={isBusy}>
                {t('integrations.reefbot.account.connect')}
              </Button>
              {replacing && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => { setReplacing(false); setPassword(''); setTouched(false); connectMutation.reset(); }}
                >
                  {t('integrations.reefbot.account.cancel')}
                </Button>
              )}
            </Inline>
          </form>
        ) : (
          <Stack gap="3">
            <Text>{t('integrations.reefbot.account.connectedAs', { email: connection?.email ?? '' })}</Text>
            {disconnectMutation.isError && <Alert compact>{t(reefbotErrorKey(disconnectMutation.error))}</Alert>}
            <Inline gap="3" wrap>
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                onClick={() => { setReplacing(true); setEmail(connection?.email ?? ''); disconnectMutation.reset(); }}
              >
                {t('integrations.reefbot.account.change')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                isLoading={disconnectMutation.isPending}
                onClick={() => { disconnectMutation.mutate(); }}
              >
                {t('integrations.reefbot.account.disconnect')}
              </Button>
            </Inline>
            <Text variant="body-sm" color="secondary">{t('integrations.reefbot.account.disconnectNote')}</Text>
          </Stack>
        )}
      </section>

      {/* ── Tank: choose, check the preview, activate ───────────────────── */}
      {choosingTank && (
        <section className="reefbot-panel__section" aria-labelledby={`${headingId}-tank`}>
          <Heading level={3} id={`${headingId}-tank`}>{t('integrations.reefbot.tank.heading')}</Heading>
          <Text color="secondary">{t('integrations.reefbot.tank.intro')}</Text>

          {tanksQuery.isPending && (
            <LiveRegion><Text color="secondary">{t('integrations.reefbot.tank.loading')}</Text></LiveRegion>
          )}

          {tanksQuery.isError && (
            <LoadErrorState
              title={t(reefbotErrorKey(tanksQuery.error))}
              titleLevel={4}
              onRetry={() => { void tanksQuery.refetch(); }}
              retryLabel={t('integrations.reefbot.retry')}
              retrying={tanksQuery.isFetching}
            />
          )}

          {tanksQuery.isSuccess && tanks.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.reefbot.tank.emptyTitle')}
              description={t('integrations.reefbot.tank.emptyBody')}
              actionLabel={t('integrations.reefbot.tank.checkAgain')}
              onAction={() => { void tanksQuery.refetch(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {tanksQuery.isSuccess && tanks.length > 0 && (
            <div className="reefbot-panel__tank-select">
              <Select
                label={t('integrations.reefbot.tank.label')}
                placeholder={t('integrations.reefbot.tank.placeholder')}
                value={selectedTank}
                onChange={(e) => { setTankChoice(e.target.value); setDrafts({}); activateMutation.reset(); }}
                disabled={isBusy}
                fullWidth
                options={tanks.map((tank) => ({ value: tank.id, label: tank.name }))}
              />
            </div>
          )}

          {previewTank !== '' && previewQuery.isPending && (
            <LiveRegion><Text color="secondary">{t('integrations.reefbot.preview.loading')}</Text></LiveRegion>
          )}

          {previewTank !== '' && previewQuery.isError && (
            <LoadErrorState
              title={t(reefbotErrorKey(previewQuery.error))}
              titleLevel={4}
              onRetry={() => { void previewQuery.refetch(); }}
              retryLabel={t('integrations.reefbot.retry')}
              retrying={previewQuery.isFetching}
            />
          )}

          {previewTank !== '' && previewQuery.isSuccess && preview.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.reefbot.preview.emptyTitle')}
              description={t('integrations.reefbot.preview.emptyBody')}
              actionLabel={t('integrations.reefbot.preview.checkAgain')}
              onAction={() => { void previewQuery.refetch(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {previewTank !== '' && previewQuery.isSuccess && preview.length > 0 && targetsQuery.isError && (
            <LoadErrorState
              title={t('integrations.testMapping.loadError')}
              titleLevel={4}
              onRetry={() => { void targetsQuery.refetch(); }}
              retryLabel={t('integrations.reefbot.retry')}
              retrying={targetsQuery.isFetching}
            />
          )}

          {previewTank !== '' && previewQuery.isSuccess && preview.length > 0 && targetsQuery.isSuccess && (
            <Stack gap="4">
              <Text color="secondary">{t('integrations.testMapping.intro')}</Text>
              <TestMappingTable rows={preview} targets={targets} drafts={drafts} onSubmitRow={setDraft} disabled={isBusy} />
              {importable === 0 ? (
                <Alert tone="warning" compact live="off">{t('integrations.testMapping.nothingToImport')}</Alert>
              ) : (
                <Text variant="body-sm" color="secondary">{t('integrations.testMapping.confirmHint')}</Text>
              )}
            </Stack>
          )}

          {activateMutation.isError && <Alert compact>{t(reefbotErrorKey(activateMutation.error))}</Alert>}

          {(previewTank !== '' || changingTank) && (
            <Inline gap="3" wrap>
              {previewTank !== '' && (
                <Button
                  type="button"
                  variant="primary"
                  isLoading={activateMutation.isPending}
                  disabled={isBusy || !previewQuery.isSuccess || !targetsQuery.isSuccess || previewInputs === null || importable === 0}
                  onClick={() => { if (previewInputs) activateMutation.mutate({ tankId: previewTank, mappings: previewInputs }); }}
                >
                  {t('integrations.testMapping.activate')}
                </Button>
              )}
              {changingTank && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => { setChangingTank(false); setTankChoice(null); setDrafts({}); activateMutation.reset(); }}
                >
                  {t('integrations.reefbot.tank.cancel')}
                </Button>
              )}
            </Inline>
          )}
        </section>
      )}

      {/* ── Test mapping of the activated tank ──────────────────────────── */}
      {showMappings && (
        <section className="reefbot-panel__section" aria-labelledby={`${headingId}-mapping`}>
          <Heading level={3} id={`${headingId}-mapping`}>{t('integrations.testMapping.heading')}</Heading>
          <Text color="secondary">{t('integrations.testMapping.intro')}</Text>

          {(mappingsQuery.isPending || (mappings.length > 0 && targetsQuery.isPending)) && (
            <LiveRegion><Text color="secondary">{t('integrations.testMapping.loading')}</Text></LiveRegion>
          )}

          {(mappingsQuery.isError || targetsQuery.isError) && (
            <LoadErrorState
              title={t('integrations.testMapping.loadError')}
              titleLevel={4}
              onRetry={() => { void mappingsQuery.refetch(); void targetsQuery.refetch(); }}
              retryLabel={t('integrations.reefbot.retry')}
              retrying={mappingsQuery.isFetching || targetsQuery.isFetching}
            />
          )}

          {mappingsQuery.isSuccess && mappings.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.testMapping.emptyTitle')}
              description={t('integrations.testMapping.emptyBody')}
              actionLabel={t('integrations.reefbot.sync.button')}
              onAction={() => { syncMutation.mutate(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {mappingsQuery.isSuccess && mappings.length > 0 && targetsQuery.isSuccess && (
            <Stack gap="4">
              {newTests > 0 && (
                <Alert
                  tone="info"
                  compact
                  live="off"
                  action={(
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      isLoading={saveMutation.isPending}
                      disabled={isBusy || mappingsInputs === null}
                      onClick={() => { if (mappingsInputs) saveMutation.mutate(mappingsInputs); }}
                    >
                      {t('integrations.testMapping.acceptSuggestions')}
                    </Button>
                  )}
                >
                  {t('integrations.testMapping.newTests', { count: newTests })}
                </Alert>
              )}
              {saveMutation.isError && <Alert compact>{t('integrations.testMapping.saveError')}</Alert>}
              <TestMappingTable
                rows={mappings}
                targets={targets}
                drafts={{}}
                disabled={isBusy}
                isSubmitting={saveMutation.isPending}
                submitError={saveMutation.isError}
                onEditStart={() => { saveMutation.reset(); }}
                onSubmitRow={(sourceKey, draft) => saveRow(sourceKey, draft)}
              />
              <Text variant="body-sm" color="secondary">{t('integrations.testMapping.saveNote')}</Text>
            </Stack>
          )}
        </section>
      )}

      {/* ── Activated tank & import ─────────────────────────────────────── */}
      {showMappings && (
        <section className="reefbot-panel__section" aria-labelledby={`${headingId}-sync`}>
          <Heading level={3} id={`${headingId}-sync`}>{t('integrations.reefbot.sync.heading')}</Heading>
          <Text variant="body-sm" color="secondary">
            {t('integrations.reefbot.sync.automatic', { tank: connection?.tankName ?? '' })}
          </Text>
          {syncMutation.isError && <Alert compact>{t(reefbotErrorKey(syncMutation.error))}</Alert>}
          <Inline gap="3" wrap>
            <Button
              type="button"
              variant="primary"
              onClick={() => { syncMutation.mutate(); }}
              isLoading={syncMutation.isPending}
              disabled={isBusy}
            >
              {syncMutation.isPending ? t('integrations.reefbot.sync.running') : t('integrations.reefbot.sync.button')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={isBusy}
              onClick={() => { setChangingTank(true); setTankChoice(null); setDrafts({}); syncMutation.reset(); }}
            >
              {t('integrations.reefbot.tank.change')}
            </Button>
          </Inline>
          <Stack gap="1">
            {lastSync ? (
              <>
                <Text variant="body-sm">
                  {t('integrations.reefbot.sync.lastSync', { date: formatDateTime(lastSync.at, i18n.language) })}
                </Text>
                {lastSync.status === 'failed' ? (
                  <Alert tone="warning" compact live="off">{t(reefbotErrorKey(lastSync.code ?? ''))}</Alert>
                ) : (
                  <Text variant="body-sm" color="secondary">
                    {t('integrations.reefbot.sync.summary', {
                      imported: lastSync.imported, updated: lastSync.updated, skipped: lastSync.skipped, unmapped: lastSync.unmapped ?? 0,
                    })}
                  </Text>
                )}
              </>
            ) : (
              <Text variant="body-sm" color="secondary">{t('integrations.reefbot.sync.never')}</Text>
            )}
          </Stack>
        </section>
      )}

      <Text variant="body-sm" color="secondary">{t('integrations.reefbot.unofficial')}</Text>
    </Card>
  );
}
